<template>
    <NuxtLayout>
        <NuxtPage />
        <Toaster />
        <CookieConsentBanner />
        <CookiePreferencesDialog />
    </NuxtLayout>
    <div v-if="isAppReady" id="app-ready" class="absolute"></div>
</template>
<script setup lang="ts">
import type { ResolvableLink, ResolvableMeta, Script } from "@unhead/vue";

const { t, locale } = useI18n();
const i18nHead = useLocaleHead({ seo: true });
const config = useRuntimeConfig();
const initializeState = useInitializeState();

const { cdnUrl, enableDatadome } = config.public;

const preconnectLinks: ResolvableLink[] = [];
const serverScripts: Script[] = [];
const isAppReady = ref(false);

if (enableDatadome) {
    const dataDomeJs = `window.ddjskey = '7012F816DF6737C9087618574AE3B2'; window.ddoptions = { endpoint: "https://dd.trustedparts.com/js/", ajaxListenerPath: true };`;
    serverScripts.push(
        { innerHTML: dataDomeJs, tagPriority: 0 },
        { src: "https://dd.trustedparts.com/tags.js", async: true, tagPriority: "high" }
    );
    preconnectLinks.push({ rel: "preconnect", href: "https://dd.trustedparts.com" });
}

useHead({
    titleTemplate: (title) => {
        if (!title) title = t("Global.TitleDefault");
        return title.includes("TrustedParts.com") ? title : title + " " + t("Global.TitleSuffix");
    },
});

await initializeState();

if (import.meta.server) {
    useHead({
        htmlAttrs: {
            lang: locale.value,
        },
        link: [
            { rel: "apple-touch-icon", sizes: "180x180", href: cdnUrl + "/apple-touch-icon.png" },
            { rel: "manifest", href: cdnUrl + "/site.webmanifest" },
            { rel: "mask-icon", href: cdnUrl + "/safari-pinned-tab.svg", color: "#00274e" },
            ...((i18nHead.value.link || []).filter(
                (link) => !(link && typeof link === "object" && "hid" in link && link.hid === "i18n-xd")
            ) as unknown as ResolvableLink[]), // remove default language alternate link
            ...preconnectLinks,
        ],
        meta: [
            { name: "msapplication-TileColor", content: "#00274e" },
            { name: "theme-color", content: "#00274e" },
            ...((i18nHead.value.meta || []) as unknown as ResolvableMeta[]),
        ],
        script: serverScripts,
    });
    // Global SEO tags common to all pages.
    useSeoMeta({
        ogType: "website",
        ogSiteName: "TrustedParts.com",
        ogImage: cdnUrl + "/assets/images/trusted-parts-logo.svg",
    });
}

onMounted(() => {
    // Signal that the app is ready, used for E2E testing to wait for interaction
    // Preferred instead of page.waitForLoadState("networkidle")
    isAppReady.value = true;

    useVersionCheck();
    useCookieConsent().initialize();
});
</script>
